import { FC, Fragment, useEffect, useState } from 'react';
import { Field, FieldProps, FormikProps } from 'formik';
import { File, UploadCloud, X } from 'react-feather';
import ReactTooltip from 'react-tooltip';
import { BsPencil } from 'react-icons/bs';
import { useQuery } from '@apollo/client';
import {
  ErrorComponent,
  FormikUploader,
  Modal,
  FieldRow,
  Spacer,
  StyledFormGroup,
  StyledFormSelectFormik,
  Button,
  Accordion,
  AccordionTitleDetail,
  Flex,
  Table,
  StyledFormPhoneFormik,
} from '../../../../../components/shared';
import { onComplete, validateSAIDNumber, formatSelectValue, isEmpty } from '../../../../../utils';
import { GET_FILE } from '../../../../../graphql';

import { client } from '../../../../../apollo';
import FileForm from '../FileForm';
import { getCitizenshipFromSAID, getGenderFromSAID } from '../../forms/PersonDetailsForm';
import { FormType } from '../FieldsForLearner';
import { SCREEN } from '../../../../../theme/breakpoints';

const isWeb = SCREEN.isWeb;

// export type FormType = {
//   caregiver: CaregiverType;
//   caregiverList: CaregiverType[];
// };

const CARE_GIVER = {
  Types: ['Parent', 'Guardian'],
  Parent: ['Birth Parent', 'Step Parent', 'Adoptive Parent'],
  Guardian: ['Legal Guardian', 'Relative', 'Other'],
  MaritalStatus: ['Single', 'Married', 'Divorced', 'Widowed'],
};

const OCCUPATIONS = [
  'Accountant',
  'Administrative Assistant/Secretary',
  'Architect',
  'Artist (Painter, Sculptor, etc.)',
  'Bank Teller',
  'Carpenter',
  'Chef/Cook',
  'Civil Servant/Government Worker',
  'Construction Worker',
  'Dentist',
  'Doctor/Physician',
  'Driver (Taxi, Delivery, etc.)',
  'Electrician',
  'Engineer (Civil, Mechanical, Electrical, etc.)',
  'Environmental Scientist',
  'Event Planner',
  'Farm Worker/Agricultural Laborer',
  'Fashion Designer',
  'Financial Advisor',
  'Fisher/Fisherman',
  'Fitness Trainer/Instructor',
  'Flight Attendant/Pilot',
  'Graphic Designer',
  'Hairdresser/Barber',
  'Interpreter/Translator',
  'IT Support Specialist',
  'Journalist',
  'Lawyer/Attorney',
  'Lecturer/Professor',
  'Librarian',
  'Marketing Manager',
  'Mechanic',
  'Miner',
  'Nurse',
  'Paramedic',
  'Pharmacist',
  'Plumber',
  'Police Officer',
  'Psychologist/Counselor',
  'Public Relations Specialist',
  'Real Estate Agent',
  'Retail Salesperson',
  'Security Guard',
  'Social Worker',
  'Software Developer/Programmer',
  'Surveyor',
  'Teacher/Educator',
  'Tour Guide',
  'Waiter/Waitress',
  'Welder',
  'Other',
];

const validateRequiredNationalId = (value: any) => {
  let error;

  const isNotValidString = validateSAIDNumber(value);

  if (isNotValidString) {
    error = isNotValidString;
  }

  if (isEmpty(value) && (!value || value === '')) {
    error = `National ID is required.`;
  }

  return error;
};

const validateMonthlyIncomeRange = (monthlyIncomeRange: any, monthlyIncome: any) => {
  let error;

  if (monthlyIncomeRange) {
    const range = computeMonthlyIncomeRange(monthlyIncome);

    if (range && range !== monthlyIncomeRange.value) {
      error = 'Monthly Income Range is not valid';
    }
  }

  return error;
};

const validate = (value: any, label: any) => {
  let error;

  if (isEmpty(value) && !value) {
    error = `${label} is required.`;
  }

  return error;
};

const computeMonthlyIncomeRange = (monthlyIncome?: number) => {
  if (monthlyIncome) {
    if (monthlyIncome <= 3500) {
      return '0To3500';
    } else if (monthlyIncome > 3500) {
      return '3501upwards';
    }
  }

  return undefined;
};

const MONTHLY_INCOME_RANGE_OPTIONS = [
  {
    label: 'R0 - R3500',
    value: '0To3500',
  },
  {
    label: 'R3501 upwards',
    value: '3501upwards',
  },
];

type Props = {
  formikProps: FormikProps<FormType>;
  institutionId?: string;
  isAdmin?: boolean;
  ministryId?: string | number;
  readOnly?: boolean;
  allNationalities: any;
  allGender: any;
};

const CaregiversCard: FC<Props> = props => {
  const { formikProps, readOnly } = props;
  const [addCaregiver, setAddCaregiverModal] = useState(false);
  // const [allNationalities, setAllNationalities] = useState<any[]>([]);
  const [validationError, setValidationError] = useState(false);

  const [uploadError, setUploadError] = useState<string>('');
  const [proofOfIncomeFile, setProofOfIncomeFile] = useState<{
    id?: string;
    name?: string;
  }>({});
  const [proofOfIncomeFileExists, setProofOfIncomeFileExists] = useState<string>('');
  const [showUploadProofOfIncomeModal, setShowUploadProofOfIncomeModal] = useState(false);

  const { data: proofOfIncomeFileData, error: proofOfIncomeFileError, loading: proofOfIncomeFileLoading } = useQuery(
    GET_FILE,
    {
      skip: !(formikProps.values.caregiver && formikProps.values.caregiver.proofOfIncomeFile),
      variables: {
        id: formikProps.values.caregiver && formikProps.values.caregiver.proofOfIncomeFile,
      },
    }
  );

  useEffect(() => {
    if (proofOfIncomeFileData && proofOfIncomeFileData.file && proofOfIncomeFileData.file.name) {
      setProofOfIncomeFileExists(proofOfIncomeFileData.file.name);
    }
  }, [proofOfIncomeFileData]);

  const saveContactToList = () => {
    if (formikProps.values.caregiver) {
      let caregiverObject = formikProps.values.caregiver;
      if (!caregiverObject.id) {
        let tick: number = new Date().getTime();
        const id = 'create' + (++tick).toString();
        caregiverObject = {
          ...caregiverObject,
          id,
        };
      }

      const newCaregiverList = formikProps.values.caregiverList
        ? formikProps.values.caregiverList.filter((c: any) => c.id !== caregiverObject.id)
        : [];

      formikProps.setFieldValue('caregiverList', [{ ...caregiverObject }, ...newCaregiverList]);
      formikProps.setFieldValue('caregiver', undefined);
    }
  };

  const deleteCaregiver = (row: any) => {
    setAddCaregiverModal(false);
    const newList = formikProps.values.caregiverList.filter((c: any) => c.id !== row.id);
    formikProps.setFieldValue('caregiverList', newList);
  };

  const validateForm = () => {
    let result = true;

    if (formikProps.values.caregiver) {
      // const careGiverType = formikProps.values.caregiver.careGiverType;
      // if (!careGiverType || isEmpty(careGiverType)) {
      //   formikProps.setFieldTouched('caregiver.careGiverType');
      //   result = false;
      // }

      // const careGiverRelationship = formikProps.values.caregiver.careGiverRelationship;
      // if (!careGiverRelationship || isEmpty(careGiverRelationship)) {
      //   formikProps.setFieldTouched('caregiver.careGiverRelationship');
      //   result = false;
      // }

      const careGiverName = formikProps.values.caregiver.careGiverName;
      if (!careGiverName || isEmpty(careGiverName)) {
        formikProps.setFieldTouched('caregiver.careGiverName');
        result = false;
      }

      const careGiverSurname = formikProps.values.caregiver.careGiverSurname;
      if (!careGiverSurname || isEmpty(careGiverSurname)) {
        formikProps.setFieldTouched('caregiver.careGiverSurname');
        result = false;
      }

      const monthlyIncomeRange = formikProps.values.caregiver.monthlyIncomeRange;
      const monthlyIncome = formikProps.values.caregiver.monthlyIncome;

      const isMonthlyIncomeRangError = validateMonthlyIncomeRange(monthlyIncomeRange, monthlyIncome);

      if (isMonthlyIncomeRangError) {
        formikProps.setFieldTouched('caregiver.monthlyIncomeRange');
        result = false;
      }

      // const careGiverGender = formikProps.values.caregiver.careGiverGender;
      // if (!careGiverGender || isEmpty(careGiverGender)) {
      //   formikProps.setFieldTouched('caregiver.careGiverGender');
      //   result = false;
      // }

      // const caregiverCitizenshipId = formikProps.values.caregiver.caregiverCitizenshipId;
      // if (!caregiverCitizenshipId || isEmpty(caregiverCitizenshipId)) {
      //   formikProps.setFieldTouched('caregiver.caregiverCitizenshipId');
      //   return false;
      // }

      // const caregiverIdentificationType = formikProps.values.caregiver.caregiverIdentificationType;
      // if (!caregiverIdentificationType || isEmpty(caregiverIdentificationType)) {
      //   formikProps.setFieldTouched('caregiver.caregiverIdentificationType');
      //   result = false;
      // }
      // const careGiverMaritalStatus = formikProps.values.caregiver.careGiverMaritalStatus;
      // if (!careGiverMaritalStatus || isEmpty(careGiverMaritalStatus)) {
      //   formikProps.setFieldTouched('caregiver.careGiverMaritalStatus');
      //   result = false;
      // }

      // const careGiverPhysicalAddress = formikProps.values.caregiver.careGiverPhysicalAddress;
      // if (!careGiverPhysicalAddress || isEmpty(careGiverPhysicalAddress)) {
      //   formikProps.setFieldTouched('caregiver.careGiverPhysicalAddress');
      //   result = false;
      // }
      // const careGiverOccupation = formikProps.values.caregiver.careGiverOccupation;

      // if (
      //   (!careGiverOccupation || isEmpty(careGiverOccupation)) &&
      //   formikProps.values.caregiver.employmentStatus &&
      //   ['Employed'].includes(formikProps.values.caregiver.employmentStatus.label)
      // ) {
      //   formikProps.setFieldTouched('caregiver.careGiverOccupation');
      //   result = false;
      // }

      // const careGiverOccupationOther = formikProps.values.caregiver.careGiverOccupationOther;

      // if (
      //   (!careGiverOccupationOther || isEmpty(careGiverOccupationOther)) &&
      //   formikProps.values.caregiver.employmentStatus &&
      //   ['Employed'].includes(formikProps.values.caregiver.employmentStatus.label) &&
      //   careGiverOccupation &&
      //   careGiverOccupation.label === 'Other'
      // ) {
      //   formikProps.setFieldTouched('caregiver.careGiverOccupationOther');
      //   result = false;
      // }

      // const careGiverEmployer = formikProps.values.caregiver.careGiverEmployer;
      // if (
      //   (!careGiverEmployer || isEmpty(careGiverEmployer)) &&
      //   formikProps.values.caregiver.employmentStatus &&
      //   ['Employed'].includes(formikProps.values.caregiver.employmentStatus.label)
      // ) {
      //   formikProps.setFieldTouched('caregiver.careGiverEmployer');
      //   result = false;
      // }
    }

    return result;
  };

  useEffect(() => {
    if (!formikProps.values.caregiver && addCaregiver) {
      formikProps.setFieldValue('caregiver', {
        caregiverCitizenshipId: formikProps.values.citizenshipId,
      });
    } else if (!addCaregiver) {
      formikProps.setFieldValue('caregiver', undefined);
    }
  }, [addCaregiver]);

  return (
    <Fragment>
      <Modal
        open={addCaregiver}
        onClose={() => {
          setAddCaregiverModal(false);
          formikProps.setFieldValue('caregiver', undefined);
          formikProps.setTouched({});
          setValidationError(false);
        }}
        heading={'Add New Caregiver'}
        width="1000px"
      >
        <Flex direction="column" size="1">
          <FieldRow>
            <Field
              name="caregiver.careGiverType"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormSelectFormik
                  {...field}
                  field={field}
                  label="Caregiver Type"
                  form={formikProps}
                  options={CARE_GIVER.Types.map((item: string) =>
                    formatSelectValue({
                      label: item,
                      value: item,
                    })
                  )}
                  mr={4}
                />
              )}
              // validate={(value: any) => validate(value, 'Caregiver Type')}
            />

            {formikProps.values.caregiver && formikProps.values.caregiver.careGiverType && (
              <Field
                name="caregiver.careGiverRelationship"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label={`${
                      formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                        ? formikProps.values.caregiver.careGiverType.label
                        : 'Caregiver'
                    } Relationship`}
                    form={formikProps}
                    options={
                      formikProps.values.caregiver &&
                      formikProps.values.caregiver.careGiverType &&
                      CARE_GIVER[formikProps.values.caregiver.careGiverType.value]
                        ? CARE_GIVER[formikProps.values.caregiver.careGiverType.value].map((item: string) =>
                            formatSelectValue({
                              label: item,
                              value: item,
                            })
                          )
                        : []
                    }
                  />
                )}
                // validate={(value: any) => validate(value, 'Caregiver Relationship')}
              />
            )}
          </FieldRow>
          <FieldRow>
            <Field
              name="caregiver.careGiverName"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormGroup
                  {...field}
                  field={field}
                  form={formikProps}
                  label={`${
                    formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                      ? formikProps.values.caregiver.careGiverType.label
                      : 'Caregiver'
                  } Name *`}
                  mr={4}
                />
              )}
              validate={(value: any) => validate(value, 'Caregiver Name')}
            />
            <Field
              name="caregiver.careGiverSurname"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormGroup
                  {...field}
                  field={field}
                  form={formikProps}
                  label={`${
                    formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                      ? formikProps.values.caregiver.careGiverType.label
                      : 'Caregiver'
                  } Surname *`}
                  mr={4}
                />
              )}
              validate={(value: any) => validate(value, 'Caregiver Surname')}
            />

            <Field
              name="caregiver.careGiverGender"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormSelectFormik
                  {...field}
                  field={field}
                  label={`${
                    formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                      ? formikProps.values.caregiver.careGiverType.label
                      : 'Caregiver'
                  } Gender`}
                  form={formikProps}
                  options={props.allGender}
                  mr={4}
                />
              )}
              // validate={(value: any) => validate(value, 'Caregiver Gender')}
            />

            <Field
              name="caregiver.caregiverCitizenshipId"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormSelectFormik
                  {...field}
                  field={field}
                  label={`${
                    formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                      ? formikProps.values.caregiver.careGiverType.label
                      : 'Caregiver'
                  } Citizenship`}
                  form={formikProps}
                  options={props.allNationalities}
                  changeHandler={(option: any) => {
                    if (option.label.toLowerCase() === 'south african') {
                      formikProps.setFieldValue('caregiverIdentificationType', {
                        label: 'RSA ID',
                        value: 'RSA ID',
                      });
                    }
                  }}
                />
              )}
              // validate={(value: any) => validate(value, 'Caregiver Citizenship')}
            />
          </FieldRow>
          <FieldRow>
            <Field
              name="caregiver.caregiverIdentificationType"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormSelectFormik
                  {...field}
                  field={field}
                  label={`${
                    formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                      ? formikProps.values.caregiver.careGiverType.label
                      : 'Caregiver'
                  } Identification Type`}
                  form={formikProps}
                  options={[
                    {
                      label: 'RSA ID',
                      value: 'RSA ID',
                    },
                    {
                      label: 'Passport',
                      value: 'Passport',
                    },
                  ]}
                  mr={4}
                />
              )}
              // validate={(value: any) => validate(value, 'Caregiver Identification Type')}
            />
            {formikProps.values.caregiver &&
              formikProps.values.caregiver.caregiverIdentificationType &&
              formikProps.values.caregiver.caregiverIdentificationType.label === 'RSA ID' && (
                <Field
                  name="caregiver.careGiverNationalId"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormGroup
                      {...field}
                      field={field}
                      form={formikProps}
                      label={`${
                        formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                          ? formikProps.values.caregiver.careGiverType.label
                          : 'Caregiver'
                      }  National ID`}
                      mr={4}
                      onChange={(event: any) => {
                        formikProps.setFieldValue('caregiver.careGiverNationalId', event.target.value);
                        const gender = getGenderFromSAID(event.target.value);
                        const citizenship = getCitizenshipFromSAID(event.target.value);

                        if (gender) {
                          const genderValue = props.allGender.find(
                            (a: any) => a.label && a.label.toLowerCase() === gender.toLowerCase()
                          );

                          formikProps.setFieldValue('caregiver.careGiverGender', genderValue);
                        }
                        if (citizenship) {
                          const citizenshipValue = props.allNationalities.find(
                            (a: any) => a.label && a.label.toLowerCase() === citizenship.toLowerCase()
                          );

                          if (citizenshipValue) {
                            formikProps.setFieldValue('caregiver.caregiverCitizenshipId', citizenshipValue);
                          }
                        }
                      }}
                    />
                  )}
                  validate={validateRequiredNationalId}
                />
              )}
            {formikProps.values.caregiver &&
              formikProps.values.caregiver.caregiverIdentificationType &&
              formikProps.values.caregiver.caregiverIdentificationType.label === 'Passport' && (
                <Field
                  name="caregiver.caregiverPassport"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormGroup
                      {...field}
                      field={field}
                      form={formikProps}
                      label={`${
                        formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                          ? formikProps.values.caregiver.careGiverType.label
                          : 'Caregiver'
                      } Passport Number`}
                      mr={4}
                    />
                  )}
                  // validate={(value: any) => validate(value, 'Passport Number')}
                />
              )}
            <Field
              name="caregiver.careGiverMaritalStatus"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormSelectFormik
                  {...field}
                  field={field}
                  label={`${
                    formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                      ? formikProps.values.caregiver.careGiverType.label
                      : 'Caregiver'
                  } Marital Status`}
                  form={formikProps}
                  options={CARE_GIVER.MaritalStatus.map((item: string) =>
                    formatSelectValue({
                      label: item,
                      value: item,
                    })
                  )}
                  mr={4}
                />
              )}
              // validate={(value: any) => validate(value, 'Caregiver Marital Status')}
            />
          </FieldRow>
          <FieldRow>
            <Field
              name="caregiver.careGiverContactNumber"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormPhoneFormik
                  {...field}
                  field={field}
                  form={formikProps}
                  label={`${
                    formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                      ? formikProps.values.caregiver.careGiverType.label
                      : 'Caregiver'
                  } Contact Number`}
                  mr={4}
                  defaultCountry="ZA"
                />
              )}
            />
            <Field
              name="caregiver.careGiverEmailAddress"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormGroup
                  {...field}
                  field={field}
                  form={formikProps}
                  label={`${
                    formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                      ? formikProps.values.caregiver.careGiverType.label
                      : 'Caregiver'
                  } Email Address`}
                />
              )}
            />
          </FieldRow>
          <FieldRow>
            <FieldRow>
              <Field
                name="caregiver.careGiverPhysicalAddress"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormGroup
                    {...field}
                    field={field}
                    form={formikProps}
                    label={`${
                      formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                        ? formikProps.values.caregiver.careGiverType.label
                        : 'Caregiver'
                    } Physical Address`}
                  />
                )}
                // validate={(value: any) => validate(value, 'Caregiver Physical Address')}
              />
            </FieldRow>
          </FieldRow>
          <FieldRow flex={0.5}>
            <Field
              name="caregiver.employmentStatus"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormSelectFormik
                  {...field}
                  field={field}
                  label="Employment Status"
                  form={formikProps}
                  options={[
                    {
                      label: 'Employed',
                      value: 'Employed',
                    },
                    {
                      label: 'Not Employed',
                      value: 'Not Employed',
                    },
                    {
                      label: 'Retired',
                      value: 'Retired',
                    },
                  ]}
                />
              )}
            />
            {formikProps.values.caregiver &&
              formikProps.values.caregiver.employmentStatus &&
              ['Employed'].includes(formikProps.values.caregiver.employmentStatus.label) && (
                <>
                  <Field
                    name="caregiver.careGiverOccupation"
                    render={({ field }: FieldProps<FormType>) => (
                      <StyledFormSelectFormik
                        {...field}
                        field={field}
                        form={formikProps}
                        label={`${
                          formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                            ? formikProps.values.caregiver.careGiverType.label
                            : 'Caregiver'
                        } Occupation`}
                        options={OCCUPATIONS.map((a: string) => ({ label: a, value: a }))}
                        ml={4}
                        mr={4}
                      />
                    )}
                    // validate={(value: any) => validate(value, 'Caregiver Occupation')}
                  />
                  {formikProps.values.caregiver &&
                    formikProps.values.caregiver.careGiverOccupation &&
                    ['Other'].includes(formikProps.values.caregiver.careGiverOccupation.label) && (
                      <Field
                        name="caregiver.careGiverOccupationOther"
                        render={({ field }: FieldProps<FormType>) => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label={`${
                              formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                                ? formikProps.values.caregiver.careGiverType.label
                                : 'Caregiver'
                            } Occupation: Other`}
                            mr={4}
                          />
                        )}
                        // validate={(value: any) => validate(value, 'Caregiver Occupation: Other')}
                      />
                    )}
                  <Field
                    name="caregiver.careGiverEmployer"
                    render={({ field }: FieldProps<FormType>) => (
                      <StyledFormGroup
                        {...field}
                        field={field}
                        form={formikProps}
                        label={`${
                          formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                            ? formikProps.values.caregiver.careGiverType.label
                            : 'Caregiver'
                        } Employer`}
                        // mr={4}
                      />
                    )}
                    // validate={(value: any) => validate(value, 'Caregiver Employer')}
                  />
                </>
              )}
          </FieldRow>

          {formikProps.values.caregiver &&
            formikProps.values.caregiver.employmentStatus &&
            ['Employed', 'Retired'].includes(formikProps.values.caregiver.employmentStatus.label) && (
              <FieldRow>
                <Field
                  name="caregiver.monthlyIncome"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormGroup
                      {...field}
                      field={field}
                      form={formikProps}
                      label="Monthly Income"
                      mr={4}
                      type="number"
                      onChange={(event: any) => {
                        try {
                          formikProps.setFieldValue('caregiver.monthlyIncome', parseInt(event.target.value));
                          const range = computeMonthlyIncomeRange(event.target.value);
                          const rangeValue = MONTHLY_INCOME_RANGE_OPTIONS.find((a: any) => a.value === range);
                          if (rangeValue) {
                            formikProps.setFieldValue('caregiver.monthlyIncomeRange', rangeValue);
                          }
                        } catch (error) {
                          console.log(error);
                        }
                      }}
                    />
                  )}
                />
                <Field
                  name="caregiver.monthlyIncomeRange"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="Monthly Income Range"
                      form={formikProps}
                      options={MONTHLY_INCOME_RANGE_OPTIONS}
                      mr={4}
                    />
                  )}
                  validate={(value: any) =>
                    validateMonthlyIncomeRange(value, formikProps.values.caregiver.monthlyIncome)
                  }
                />
                <Field
                  name="caregiver.incomeSourceDocument"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="Income Source Document"
                      form={formikProps}
                      options={[
                        {
                          label: 'Affidavit',
                          value: 'Affidavit',
                        },
                        {
                          label: 'Proof of Income',
                          value: 'Proof of Income',
                        },
                      ]}
                      mr={4}
                    />
                  )}
                />

                {/* <Button
            onClick={() => setShowUploadModal(!showUploadModal)}
            text={` Upload Proof of Income`}
            type="button"
            bg="success"
            p={0}
            mr={10}
            mt={4}
            width="70px"
            icon={<UploadCloud size={15} color="white" />}
          /> */}
                {/* <FieldRow> */}
                {formikProps.values.caregiver && formikProps.values.caregiver.incomeSourceDocument && (
                  <Field
                    name="caregiver.proofOfIncomeFile"
                    render={({ field }: FieldProps<FormType>) => (
                      <Fragment>
                        {uploadError ? <ErrorComponent message={uploadError} /> : null}

                        {formikProps.values.caregiver.proofOfIncomeFile && (
                          <Fragment>
                            {!proofOfIncomeFileLoading && !proofOfIncomeFileError && proofOfIncomeFileExists && (
                              <Fragment>
                                <Button
                                  bg="success"
                                  icon={<File />}
                                  isIcon={true}
                                  mr={1}
                                  ml={3}
                                  p={0}
                                  type="button"
                                  text="Download File"
                                  width="40px"
                                  style={{
                                    fontSize: '10px',
                                    marginTop: '24px',
                                  }}
                                  onClick={() => {
                                    setProofOfIncomeFile(proofOfIncomeFileData.file);
                                    setShowUploadProofOfIncomeModal(!showUploadProofOfIncomeModal);
                                  }}
                                />
                                <div
                                  style={{
                                    marginTop: '35px',
                                    marginRight: '20px',
                                    whiteSpace: 'nowrap',
                                    width: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {proofOfIncomeFileData &&
                                    proofOfIncomeFileData.file &&
                                    proofOfIncomeFileData.file.name}
                                </div>
                              </Fragment>
                            )}
                          </Fragment>
                        )}

                        <Button
                          type="button"
                          id="uppy-open"
                          text={`Upload ${(formikProps.values.caregiver.incomeSourceDocument as any).label}`}
                          hidden={!!proofOfIncomeFileExists}
                          mr={4}
                          icon={<UploadCloud size={10} color="white" />}
                          style={{
                            fontSize: '10px',
                            marginTop: '24px',
                            width: '250px',
                          }}
                        />

                        <FormikUploader
                          onComplete={onComplete}
                          setUploadError={setUploadError}
                          uploadError={uploadError}
                          client={client}
                          {...field}
                          field={field}
                          form={formikProps}
                        />
                      </Fragment>
                    )}
                  />
                )}
              </FieldRow>
            )}
          <FieldRow>
            <Field
              name="caregiver.childResidesWithThisCaregive"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormSelectFormik
                  {...field}
                  field={field}
                  label={`Child resides with this ${
                    formikProps.values.caregiver && formikProps.values.caregiver.careGiverType
                      ? formikProps.values.caregiver.careGiverType.label
                      : 'caregiver'
                  } `}
                  form={formikProps}
                  options={[
                    {
                      label: 'Yes',
                      value: true,
                    },
                    {
                      label: 'No',
                      value: false,
                    },
                  ]}
                  mr={4}
                />
              )}
            />
            <Field
              name="caregiver.accountPayer"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormSelectFormik
                  {...field}
                  field={field}
                  label="Account Payer"
                  form={formikProps}
                  options={[
                    {
                      label: 'Yes',
                      value: true,
                    },
                    {
                      label: 'No',
                      value: false,
                    },
                  ]}
                />
              )}
            />
          </FieldRow>
          <Spacer spacing={20} />

          <FieldRow justifyContent={isWeb ? 'flex-end' : 'center'}>
            {validationError && Object.keys(formikProps.errors).find((a: any) => a === 'caregiver') && (
              <Flex direction="row" size="1" justify="flex-start" alignItems="center">
                <div
                  style={{
                    marginTop: '10px',
                    color: '#cc0000',
                    textAlign: 'right',
                    fontWeight: 200,
                    fontSize: '20px',
                    textTransform: 'none',
                  }}
                >
                  {' '}
                  Scroll up to see the error message(s)
                </div>
              </Flex>
            )}
            <Flex direction="row" size="1" justify={isWeb ? 'flex-end' : 'center'} alignItems="center">
              <Flex direction={isWeb ? 'row' : 'column'} size={isWeb ? '0.5' : '1'} flexWrap="nowrap">
                <Button
                  type="button"
                  text="Cancel"
                  mr={3}
                  mb={isWeb ? 0 : 2}
                  onClick={() => {
                    formikProps.setFieldValue('caregiver', undefined);
                    setAddCaregiverModal(false);
                    setValidationError(false);
                    formikProps.setTouched({});
                  }}
                />
                <Button
                  type="button"
                  text="Save"
                  onClick={() => {
                    if (validateForm()) {
                      setValidationError(false);
                      saveContactToList();
                      setAddCaregiverModal(false);
                      formikProps.setTouched({});
                    } else {
                      setValidationError(true);
                    }
                  }}
                />
              </Flex>
            </Flex>
          </FieldRow>
        </Flex>
      </Modal>
      <Modal
        open={showUploadProofOfIncomeModal}
        onClose={() => {
          setShowUploadProofOfIncomeModal(!showUploadProofOfIncomeModal);
        }}
        heading={`Manage File: ${proofOfIncomeFile.name}`}
      >
        <FileForm
          file={proofOfIncomeFile}
          refetchQueryFunction={() => {
            return [
              {
                query: GET_FILE,
                variables: {
                  id: formikProps.values.caregiver && formikProps.values.caregiver.proofOfIncomeFile,
                },
              },
            ];
          }}
          onDone={() => {
            setProofOfIncomeFile({});
            setShowUploadProofOfIncomeModal(!showUploadProofOfIncomeModal);
          }}
          onDelete={() => {
            setProofOfIncomeFileExists('');
          }}
        />
      </Modal>
      <Accordion
        title={`Caregivers (${formikProps.values.caregiverList ? formikProps.values.caregiverList.length : 0})`}
        titleDetail={
          !readOnly ? (
            <AccordionTitleDetail text={'+ New'} propagateOnClick={false} onClick={() => setAddCaregiverModal(true)} />
          ) : (
            ''
          )
        }
      >
        <Table
          showHeader={false}
          defaultPageSize={5}
          minRows={1}
          padding={0}
          data={formikProps.values.caregiverList ? formikProps.values.caregiverList : []}
          onRowClick={(rowData: any) => {
            if (!isWeb && rowData && rowData.original) {
              formikProps.setFieldValue('caregiver', rowData.original);
              setAddCaregiverModal(true);
            }
          }}
          columns={[
            {
              id: 'firstName',
              Header: 'Full Name',
              accessor: (row: any) => (
                <div>
                  {row.careGiverName} {` `} {row.careGiverSurname}
                </div>
              ),
            },
            {
              id: 'type',
              Header: 'Type',
              accessor: (row: any) => <div>{row.careGiverType && row.careGiverType.label}</div>,
            },
            {
              id: 'careGiverRelationship',
              Header: 'Relationship',
              show: isWeb,
              accessor: (row: any) => <div>{row.careGiverRelationship && row.careGiverRelationship.label}</div>,
            },
            {
              id: 'careGiverContactNumber',
              Header: 'Contact Number',
              show: isWeb,
              accessor: (row: any) => <div>{row.careGiverContactNumber}</div>,
            },
            {
              id: 'actions',
              accessor: (row: any) => (
                <Flex direction="row" size="1" justify="flex-end">
                  <Fragment>
                    <a data-tip={true} data-for={`${row.id}-edit-caregiver`}>
                      <Button
                        bg="warning"
                        icon={<BsPencil />}
                        isIcon={true}
                        mr={1}
                        p={0}
                        type="button"
                        width="40px"
                        onClick={e => {
                          e.stopPropagation();
                          formikProps.setFieldValue('caregiver', row);
                          setAddCaregiverModal(true);
                        }}
                      />
                    </a>
                    <ReactTooltip id={`${row && row.id}-edit-caregiver`} effect="solid">
                      Edit Caregiver
                    </ReactTooltip>
                  </Fragment>
                  <Fragment>
                    <a data-tip={true} data-for={`${row.id}-delete-caregiver`}>
                      <Button
                        bg="error"
                        icon={<X />}
                        type="button"
                        isIcon={true}
                        p={0}
                        width="40px"
                        onClick={e => {
                          e.stopPropagation();
                          deleteCaregiver(row);
                        }}
                      />
                    </a>
                    <ReactTooltip id={`${row && row.id}-delete-caregiver`} effect="solid">
                      Delete Caregiver
                    </ReactTooltip>
                  </Fragment>
                </Flex>
              ),
            },
          ]}
          manual={true}
          onButtonPress={() => {
            setAddCaregiverModal(true);
          }}
          buttonText="Add New"
        />
      </Accordion>
    </Fragment>
  );
};

export default CaregiversCard;
